import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { PrimaryLinks } from './PrimaryLinks';
import { Logo, LogoProps } from '../Logo';
import { Editions, EditionDataProps } from './Editions';
import { CtaButton, CtaDataProps } from './CtaButton';
import { HeaderLinksDataProps } from '../Header';
import { Menu } from './menu/Menu';
import { primaryHeaderHeight } from '../header.utils';
import { MenuLinkDataProps } from './menu/links/MenuLink';
import { ErrorBoundary } from '../../../../errorHandling/ErrorBoundary';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';
import { VideoLogo, InjectedVideoLogoApiProps } from './VideoLogo';
import { createStyleForBoxShadow } from '../../../../../theming/boxShadow';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';

const componentConfigThemeKey = 'primaryHeader';

export interface PrimaryHeaderDataProps {
  links: HeaderLinksDataProps[] | null
  logo: LogoProps
  editions: EditionDataProps[] | null
  cta: CtaDataProps | null
  menu: MenuLinkDataProps[] | null
  videoLogo: InjectedVideoLogoApiProps | null
  pagePath: string,
  editionEndpoint: string
}

export interface PrimaryHeaderThemeProps {
  baseUnit: BaseUnit
  backgroundColor: string
  boxShadowTypeNormal: string
}

const getStyle = (baseUnit: BaseUnit, backgroundColor: string) => {
  return StyleSheet.create({
    header: {
      backgroundColor,
      position: 'relative',
      height: `${primaryHeaderHeight}px`,
      display: 'flex',
      justifyContent: 'space-between',
      zIndex: 100,
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      gridColumnGap: `${baseUnit}px`,
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.LARGE]: {
        paddingRight: `${baseUnit}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        paddingRight: `${baseUnit / 2}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        paddingRight: `${baseUnit / 2}px`,
      },
    },
    itemsLeftContainer: {
      paddingLeft: `${baseUnit}px`,
    },
  });
};

type propsType = PrimaryHeaderThemeProps & PrimaryHeaderDataProps;

export const PrimaryHeaderComponent: React.FunctionComponent<propsType> = props => {
  const { baseUnit, backgroundColor, links, logo, editions, boxShadowTypeNormal, cta, menu, videoLogo, pagePath, editionEndpoint } = props;
  const style = getStyle(baseUnit, backgroundColor);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });

  return (
    <div className={css(boxShadowStyle.style, style.header)}>
      <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
        {menu ? <Menu menu={menu} editions={editions} editionEndpoint={editionEndpoint} /> : null}
        {videoLogo ? <VideoLogo {...videoLogo} href={logo.href} /> : <Logo {...logo} />}
        {links ? <PrimaryLinks links={links} pagePath={pagePath} /> : null}
      </div>
      <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
        {cta ? <CtaButton {...cta} /> : null}
        {editions ? <Editions editions={editions} editionEndpoint={editionEndpoint} /> : null}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: PrimaryHeaderThemeProps) => {
  return {
    baseUnit: theme.baseUnit,
    backgroundColor: theme.backgroundColor,
    boxShadowTypeNormal: theme.boxShadowTypeNormal,
  };
};

export const PrimaryHeader = (props: PrimaryHeaderDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><PrimaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
